import { lazy, Suspense } from 'react'
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom'
import { AuthProvider, useAuth } from './lib/auth'
import { I18nProvider } from './lib/i18n'
import { ThemeProvider } from './lib/theme.jsx'
import { ToastProvider } from './components/ui/Toast'
import AppShell from './components/layout/AppShell'
import ErrorBoundary from './components/layout/ErrorBoundary'
import RouteFallback from './components/layout/RouteFallback'

/*
 * Routing.
 *
 * Screens are lazily loaded per route. The bundle that matters most is the one
 * an employee on a mine-site connection downloads to file a grievance — they
 * should not be paying for the admin console, the charting library or the ICC
 * case view to do it.
 */

const Login            = lazy(() => import('./pages/Login'))
const Dashboard        = lazy(() => import('./pages/Dashboard'))
const ChangePassword   = lazy(() => import('./pages/ChangePassword'))

const MyGrievances     = lazy(() => import('./pages/grievances/MyGrievances'))
const FileGrievance    = lazy(() => import('./pages/grievances/FileGrievance'))
const MyGrievanceView  = lazy(() => import('./pages/grievances/MyGrievanceView'))
const GrievanceQueue   = lazy(() => import('./pages/grievances/GrievanceQueue'))
const GrievanceDetail  = lazy(() => import('./pages/grievances/GrievanceDetail'))
const Enquiries        = lazy(() => import('./pages/grievances/Enquiries'))

const DisciplinaryQueue = lazy(() => import('./pages/discipline/DisciplinaryQueue'))
const DisciplinaryCase  = lazy(() => import('./pages/discipline/DisciplinaryCase'))
const MyDisciplinary    = lazy(() => import('./pages/discipline/MyDisciplinary'))

const SuggestionBox    = lazy(() => import('./pages/feedback/SuggestionBox'))
const FeedbackConsole  = lazy(() => import('./pages/feedback/FeedbackConsole'))

const Surveys          = lazy(() => import('./pages/surveys/Surveys'))
const SurveyResults    = lazy(() => import('./pages/surveys/SurveyResults'))
const TakeSurvey       = lazy(() => import('./pages/surveys/TakeSurvey'))

const PoshDashboard    = lazy(() => import('./pages/posh/PoshDashboard'))
const PoshComplaints   = lazy(() => import('./pages/posh/PoshComplaints'))
const IccRoster        = lazy(() => import('./pages/posh/IccRoster'))
const TrainingCompliance = lazy(() => import('./pages/posh/TrainingCompliance'))
const AnnualReport     = lazy(() => import('./pages/posh/AnnualReport'))
const PoshCase         = lazy(() => import('./pages/posh/PoshCase'))
const FilePoshComplaint = lazy(() => import('./pages/posh/FilePoshComplaint'))

const WhistleblowerConsole = lazy(() => import('./pages/whistleblower/WhistleblowerConsole'))
const WhistleblowerCase    = lazy(() => import('./pages/whistleblower/WhistleblowerCase'))
const WhistleblowerRoster  = lazy(() => import('./pages/whistleblower/WhistleblowerRoster'))
const ReportWrongdoing     = lazy(() => import('./pages/whistleblower/ReportWrongdoing'))
const TrackReport          = lazy(() => import('./pages/whistleblower/TrackReport'))

const Reports          = lazy(() => import('./pages/Reports'))
const People           = lazy(() => import('./pages/admin/People'))
const SettingsPage     = lazy(() => import('./pages/admin/Settings'))
const Profile          = lazy(() => import('./pages/Profile'))
const NotFound         = lazy(() => import('./pages/NotFound'))

export default function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <I18nProvider>
          <ToastProvider>
            <BrowserRouter>
              <AuthProvider>
                <Suspense fallback={<RouteFallback />}>
                  <AppRoutes />
                </Suspense>
              </AuthProvider>
            </BrowserRouter>
          </ToastProvider>
        </I18nProvider>
      </ThemeProvider>
    </ErrorBoundary>
  )
}

function AppRoutes() {
  const { restoring } = useAuth()

  // Until the stored token has been checked we do not know whether this person
  // is signed in. Rendering the login screen meanwhile flashes a sign-in form
  // at someone who is already authenticated on every refresh.
  if (restoring) return <RouteFallback />

  return (
    <Routes>
      {/* ── Public ─────────────────────────────────────────────────────────
          Deliberately outside the authenticated shell. Reporting wrongdoing
          and checking an anonymous report must work without an account —
          requiring a login would attach an identity to an anonymous report and
          defeat the entire mechanism. */}
      <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
      <Route path="/report-wrongdoing" element={<ReportWrongdoing />} />
      <Route path="/track-report" element={<TrackReport />} />
      <Route path="/survey/:token" element={<TakeSurvey />} />

      {/* ── Authenticated ──────────────────────────────────────────────────
          change-password sits outside the shell so someone forced to change a
          provisioning password cannot navigate away from it. */}
      <Route path="/change-password" element={<RequireAuth allowPasswordChange><ChangePassword /></RequireAuth>} />

      <Route element={<RequireAuth><AppShell /></RequireAuth>}>
        <Route index element={<Dashboard />} />

        <Route path="my/grievances" element={<MyGrievances />} />
        <Route path="my/grievances/new" element={<FileGrievance />} />
        <Route path="my/grievances/:id" element={<MyGrievanceView />} />

        <Route path="grievances" element={<RequirePermission any={['GRV-01', 'GRV-07']}><GrievanceQueue /></RequirePermission>} />
        <Route path="grievances/assigned" element={<GrievanceQueue mine />} />
        <Route path="grievances/:id" element={<GrievanceDetail />} />
        <Route path="enquiries" element={<RequirePermission any={['GRV-06']}><Enquiries /></RequirePermission>} />

        <Route path="disciplinary" element={<RequirePermission any={['DSC-01']}><DisciplinaryQueue /></RequirePermission>} />
        <Route path="disciplinary/:id" element={<RequirePermission any={['DSC-01']}><DisciplinaryCase /></RequirePermission>} />

        {/* No permission gate. An employee's right to see what is alleged
            against them and to answer it is the basis on which any later order
            stands up — it cannot be something an administrator switches on. */}
        <Route path="my/disciplinary" element={<MyDisciplinary />} />


        <Route path="suggestions" element={<SuggestionBox />} />
        <Route path="feedback" element={<RequirePermission any={['FBK-01']}><FeedbackConsole /></RequirePermission>} />

        <Route path="surveys" element={<RequirePermission any={['SRV-01']}><Surveys /></RequirePermission>} />
        <Route path="surveys/instances/:id" element={<RequirePermission any={['SRV-01']}><SurveyResults /></RequirePermission>} />

        <Route path="posh/file" element={<FilePoshComplaint />} />

        {/* No RequirePermission on any of these. Access is a seat on the
            Committee, not a permission - the server returns 404 to everyone
            else, including an administrator who granted themselves POSH-01. */}
        <Route path="posh" element={<Navigate to="/posh/dashboard" replace />} />
        <Route path="posh/dashboard" element={<PoshDashboard />} />
        <Route path="posh/icc" element={<IccRoster />} />
        <Route path="posh/complaints" element={<PoshComplaints />} />
        <Route path="posh/training" element={<TrainingCompliance />} />
        <Route path="posh/annual-report" element={<AnnualReport />} />
        <Route path="posh/cases/:id" element={<PoshCase />} />

        <Route path="whistleblower" element={<WhistleblowerConsole />} />
        <Route path="whistleblower/roster" element={<WhistleblowerRoster />} />
        <Route path="whistleblower/reports/:id" element={<WhistleblowerCase />} />

        <Route path="reports" element={<RequirePermission any={['RPT-01']}><Reports /></RequirePermission>} />
        <Route path="people" element={<RequirePermission any={['ORG-01']}><People /></RequirePermission>} />
        <Route path="settings" element={<RequirePermission any={['ADM-01', 'ADM-03', 'GRV-04']}><SettingsPage /></RequirePermission>} />
        <Route path="settings/profile" element={<Profile />} />
      </Route>

      <Route path="*" element={<NotFound />} />
    </Routes>
  )
}

function RequireAuth({ children, allowPasswordChange = false }) {
  const { isAuthenticated, mustChangePassword } = useAuth()
  const location = useLocation()

  if (!isAuthenticated) {
    // Remember where they were headed so sign-in returns them there rather
    // than dumping everyone on the dashboard.
    return <Navigate to="/login" state={{ from: location }} replace />
  }

  // A provisioning password must stop working the moment it is used, so the
  // change is enforced here rather than merely suggested.
  if (mustChangePassword && !allowPasswordChange) {
    return <Navigate to="/change-password" replace />
  }

  return children
}

function PublicOnly({ children }) {
  const { isAuthenticated } = useAuth()

  return isAuthenticated ? <Navigate to="/" replace /> : children
}

/**
 * Client-side permission gate.
 *
 * Cosmetic only — the server refuses regardless. This exists so someone who
 * follows a stale bookmark gets a clear explanation instead of a screen that
 * renders and then fills with 403 errors.
 */
function RequirePermission({ any, children }) {
  const { can } = useAuth()

  if (!can(...any)) {
    return (
      <div className="mx-auto max-w-md py-16 text-center">
        <h1 className="font-display text-lg font-semibold text-[var(--color-ink-900)]">
          You do not have access to this page
        </h1>
        <p className="mt-2 text-sm text-[var(--color-ink-500)]">
          If you believe you should, ask your portal administrator to review your role.
        </p>
      </div>
    )
  }

  return children
}
